import {useCallback, useRef} from 'react';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {downloadDataGridExport, handleApiError, notificationTypes} from '@dms/shared';

import {ApiError, parseDate} from 'shared/web';

import {formatDataGridExportDate, StartedExport} from 'features/eag-grid';

export function useDownloadExportedFiles() {
  // Tracked as a ref so adding/removing pending export ids does not re-render
  // DataGridContainer. The Set is only read inside the notification callback
  // to filter exports started by other tabs/sessions; it is never rendered.
  const pendingExportIdsRef = useRef<Set<string>>(new Set());

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== notificationTypes.DATAGRID_EXPORT) {
      return;
    }

    const {exportId, dataGridCode, exportedAt} = notification.payload;

    if (!pendingExportIdsRef.current.has(exportId)) {
      return;
    }

    downloadDataGridExport({
      exportId,
      gridCode: dataGridCode,
      exportedAt: exportedAt ? formatDataGridExportDate(parseDate(exportedAt)) : '',
    })
      .then(() => {
        pendingExportIdsRef.current.delete(exportId);
      })
      .catch((error: ApiError) => handleApiError(error));
  });

  const handleExportSuccessfullyStarted = useCallback((startedExport: StartedExport) => {
    pendingExportIdsRef.current.add(startedExport.exportId);
  }, []);

  return {
    handleExportSuccessfullyStarted,
  };
}
